import { z } from "zod";

import { isAcceptedNmbiNumber, NMBI_NUMBER_ERROR } from "utils/validators";

export const TITLE_OPTIONS = [
  { value: "Mr.", label: "Mr" },
  { value: "Ms", label: "Ms" },
  { value: "Mrs", label: "Mrs" },
  { value: "Miss", label: "Miss" },
  { value: "Dr", label: "Dr" },
  { value: "Master", label: "Master" },
];

export const ROLE_OPTIONS = [
  { value: "USER", label: "User" },
  { value: "ADMIN", label: "Admin" },
  { value: "DOCTOR", label: "Doctor" },
  { value: "OFFICE", label: "Office" },
  { value: "API", label: "API" },
];

export const STAFF_ROLE_OPTIONS = [
  { value: "DOCTOR", label: "Doctor" },
  { value: "NURSE", label: "Nurse" },
  { value: "ADMIN", label: "Admin" },
  { value: "TECH", label: "Tech Support" },
];

export const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
];

// Loose on purpose: staff numbers are entered in several formats and the
// backend does not normalise them.
const MOBILE_PATTERN = /^\+?[\d\s\-().]{7,15}$/;

export const userFormSchema = z.object({
  title: z.string(),
  firstName: z.string().trim().min(1, "Please enter a first name"),
  lastName: z.string().trim().min(1, "Please enter a last name"),
  mainRole: z.string().min(1, "Please select a role"),
  staffRole: z.string(),
  specialism: z.string(),
  professionalMedicalNumber: z.string(),
  // All three identifiers are edited here, not just the MCN. The GMS number kept disappearing because
  // saves wrote NULL over omitted fields (KIN-1935); since KIN-1724 an omitted field is kept and only
  // "" clears it, so a field this form shows must be sent as the user left it.
  gmsNumber: z.string(),
  deaspPanelNumber: z.string(),
  // A nurse's NMBI number is kept apart from the Medical Council number, which referrals and lab
  // orders send as a doctor's MCN (KIN-2111). The backend refuses a malformed one with a 400.
  nmbiNumber: z.string().refine(isAcceptedNmbiNumber, {
    message: NMBI_NUMBER_ERROR,
  }),
  qualificationLetters: z.string(),
  mobile: z
    .string()
    .refine((value) => !value || MOBILE_PATTERN.test(value), {
      message: "Please enter a valid mobile number",
    }),
  homeNumber: z.string(),
  email: z
    .string()
    .trim()
    .min(1, "Please enter an email")
    .email("Please enter a valid email"),
  gender: z.string().min(1, "Please select a gender"),
  bio: z.string(),
  systemAccess: z.boolean(),
  showOnTeam: z.boolean(),
  roster: z.boolean(),
});

export const emptyUser = () => ({
  title: TITLE_OPTIONS[0].value,
  firstName: "",
  lastName: "",
  mainRole: ROLE_OPTIONS[0].value,
  staffRole: "",
  specialism: "",
  professionalMedicalNumber: "",
  gmsNumber: "",
  deaspPanelNumber: "",
  nmbiNumber: "",
  qualificationLetters: "",
  mobile: "",
  homeNumber: "",
  email: "",
  gender: "",
  bio: "",
  systemAccess: false,
  showOnTeam: false,
  roster: false,
});

/** Maps a staff record from the list into the form's shape. */
export const toFormValues = (staff) => {
  if (!staff) return emptyUser();

  const empty = emptyUser();

  return {
    ...empty,
    title: staff.title || empty.title,
    firstName: staff.firstName ?? "",
    lastName: staff.lastName ?? "",
    mainRole: staff.mainRole || empty.mainRole,
    staffRole: staff.staffRole ?? "",
    specialism: staff.specialism ?? "",
    professionalMedicalNumber: staff.professionalMedicalNumber ?? "",
    gmsNumber: staff.gmsNumber ?? "",
    deaspPanelNumber: staff.deaspPanelNumber ?? "",
    nmbiNumber: staff.nmbiNumber ?? "",
    qualificationLetters: staff.qualificationLetters ?? "",
    mobile: staff.mobile ?? "",
    homeNumber: staff.homeNumber ?? "",
    email: staff.email ?? "",
    gender: staff.gender ?? "",
    bio: staff.bio ?? "",
    systemAccess: !!staff.systemAccess,
    showOnTeam: !!staff.showOnTeam,
    roster: !!staff.roster,
  };
};
